// The fields a person can fix right on an assistant card before applying
// it, per kind of card. An edit changes the card's payload (what is saved),
// its title and its rows together, and marks the card as edited so the
// outcome sent back says the AI was close but not right.

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const FIELD_SPECS = {
  task: [
    { key: 'title', label: 'Title', required: true, max: 200, asTitle: true },
    { key: 'due_date', label: 'Due', type: 'date', emptyText: 'No due date' },
  ],
  activity: [
    { key: 'summary', label: 'Summary', multiline: true, required: true, max: 2000 },
    { key: 'activity_date', label: 'When', type: 'date', required: true },
  ],
  contact: [
    { key: 'name', label: 'Name', required: true, max: 120 },
    { key: 'title', label: 'Title', max: 120, emptyText: 'Add a job title' },
    { key: 'email', label: 'Email', max: 200, emptyText: 'Add an email' },
    { key: 'phone', label: 'Phone', max: 50, emptyText: 'Add a phone number' },
  ],
  account: [
    { key: 'name', label: 'Name', required: true, max: 200 },
    { key: 'industry', label: 'Industry', max: 120, emptyText: 'Add an industry' },
    { key: 'website', label: 'Website', max: 300, emptyText: 'Add a website' },
  ],
  opportunity: [
    { key: 'name', label: 'Name', max: 200, emptyText: 'Named after the account' },
    { key: 'expected_close_date', label: 'Expected close', type: 'date', emptyText: 'End of the quarter' },
  ],
};

const titleFor = (kind, p, fallback) => {
  if (kind === 'task') return p.title;
  if (kind === 'contact') return p.account_name ? `${p.name} at ${p.account_name}` : p.name;
  if (kind === 'account') return `New account: ${p.name}`;
  if (kind === 'opportunity') return p.name ? `New opportunity: ${p.name}` : `New opportunity on ${p.account_name || 'the account'}`;
  return fallback;
};

/** The editable rows of a pending card, with the values it would save now. */
export function fieldsFor(proposal) {
  const specs = FIELD_SPECS[proposal.kind];
  if (!specs) return null;
  const p = proposal.payload || {};
  return specs.map((s) => ({ ...s, value: p[s.key] === null || p[s.key] === undefined ? '' : String(p[s.key]) }));
}

/** The labels of a card's rows that its editable fields already show. */
export function coveredLabels(proposal) {
  return new Set((FIELD_SPECS[proposal.kind] || []).map((s) => s.label));
}

/**
 * The card with one field changed. Throws, with words for the person, when
 * the value cannot be saved, so the field stays open with the reason.
 */
export function editProposalField(proposal, key, raw) {
  const spec = (FIELD_SPECS[proposal.kind] || []).find((s) => s.key === key);
  if (!spec) throw new Error('This cannot be changed here.');
  const value = raw === null || raw === undefined ? '' : String(raw).trim();
  if (!value && spec.required) throw new Error(`${spec.label} is needed.`);
  if (value && spec.type === 'date' && !DAY.test(value)) throw new Error('Pick a date.');
  if (spec.max && value.length > spec.max) throw new Error(`${spec.label} can be at most ${spec.max} characters.`);
  if (key === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) throw new Error('That does not look like an email address.');

  const stored = value || (proposal.kind === 'account' || proposal.kind === 'opportunity' ? '' : null);
  const payload = { ...(proposal.payload || {}), [key]: stored };
  const editedFields = Array.from(new Set([...(proposal.editedFields || []), key]));
  return {
    ...proposal,
    payload,
    title: titleFor(proposal.kind, payload, proposal.title),
    description: proposal.kind === 'activity' ? payload.summary : proposal.description,
    edited: true,
    editedFields,
  };
}
